import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import StudentPicker from "../../components/StudentPicker";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { listCashOnlyLedgers } from "../../api/accountApi";
import {
  generatePayslip,
  batchGeneratePayslips,
  listPendingPayslips,
  deletePayslip,
  collectPayslips,
} from "../../api/payslipApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const feeHeadApi = createLookupApi("fee-heads");
const feeSubHeadApi = createLookupApi("fee-subheads");
const feeWaiverApi = createLookupApi("fee-waivers");
const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({
  key: Math.random(),
  duePayment: false,
  feeHeadId: "",
  feeSubHeadId: "",
  requiredAmount: "",
  payableAmount: "",
  paidAmount: "",
  fineAmount: "0",
  waiverId: "",
  waiverAmount: "0",
});

/**
 * Payslip management — folds digicampusbd's Create Single / Multiple Create
 * / Section Create and ID-wise / Section-wise / Class-wise Collection pages
 * into one, tab-switched (same generation/collection logic underneath
 * regardless of how the student list was narrowed — see PayslipService).
 *
 * Pending & Collect tab's Debit Ledger picker uses `listCashOnlyLedgers`
 * (Cash & Cash Equivalent only, category_default_id {102}), not the generic
 * unfiltered list — see that tab's own useEffect comment and MIGRATION.md.
 */
export default function PayslipPage() {
  const [tab, setTab] = useState("single");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Payslip (Fee Pay-in Slip)</h1>
      </div>
      <Tabs
        tabs={[
          { key: "single", label: "Generate Single" },
          { key: "batch", label: "Batch Generate" },
          { key: "collect", label: "Pending & Collect" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "single" && <SingleGenerateTab setError={setError} setSuccess={setSuccess} />}
      {tab === "batch" && <BatchGenerateTab setError={setError} setSuccess={setSuccess} />}
      {tab === "collect" && <CollectTab setError={setError} setSuccess={setSuccess} />}
    </MainLayout>
  );
}

function SingleGenerateTab({ setError, setSuccess }) {
  const [student, setStudent] = useState(null);
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [feeWaivers, setFeeWaivers] = useState([]);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([feeHeadApi.list(), feeSubHeadApi.list(), feeWaiverApi.list()]).then(([heads, subHeads, waivers]) => {
      setFeeHeads(heads);
      setFeeSubHeads(subHeads);
      setFeeWaivers(waivers);
    });
  }, []);

  const setLine = (key, field, value) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!student) {
      setError("Select a student first.");
      return;
    }
    const usable = lines.filter((l) => l.feeHeadId && l.feeSubHeadId);
    if (usable.length === 0) {
      setError("Add at least one fee line.");
      return;
    }
    setSaving(true);
    try {
      await generatePayslip({
        identificationId: student.identificationId,
        note,
        lines: usable.map((l) => ({
          feeHeadId: Number(l.feeHeadId),
          feeSubHeadId: Number(l.feeSubHeadId),
          duePayment: l.duePayment,
          requiredAmount: Number(l.requiredAmount) || 0,
          payableAmount: Number(l.payableAmount) || 0,
          paidAmount: Number(l.paidAmount) || 0,
          fineAmount: Number(l.fineAmount) || 0,
          waiverId: l.waiverId ? Number(l.waiverId) : null,
          waiverAmount: Number(l.waiverAmount) || 0,
        })),
      });
      setSuccess("Payslip generated. Collect it from the Pending & Collect tab.");
      setLines([emptyLine(), emptyLine()]);
      setNote("");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to generate payslip.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StudentPicker onSelect={setStudent} />
      {student && (
        <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16 }}>
          <label>Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="table-scroll" style={{ marginTop: 12 }}>
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Due?</th>
                <th>Fee Head</th>
                <th>Sub-Head</th>
                <th>Required (due)</th>
                <th>Payable</th>
                <th>Paid</th>
                <th>Fine</th>
                <th>Waiver</th>
                <th>Waiver Amt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key}>
                  <td>
                    <input type="checkbox" checked={line.duePayment} onChange={(e) => setLine(line.key, "duePayment", e.target.checked)} />
                  </td>
                  <td>
                    <select value={line.feeHeadId} onChange={(e) => setLine(line.key, "feeHeadId", e.target.value)}>
                      <option value="">Select</option>
                      {feeHeads.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={line.feeSubHeadId} onChange={(e) => setLine(line.key, "feeSubHeadId", e.target.value)}>
                      <option value="">Select</option>
                      {feeSubHeads.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" style={{ width: 90 }} disabled={!line.duePayment} value={line.requiredAmount} onChange={(e) => setLine(line.key, "requiredAmount", e.target.value)} />
                  </td>
                  <td>
                    <input type="number" style={{ width: 90 }} disabled={line.duePayment} value={line.payableAmount} onChange={(e) => setLine(line.key, "payableAmount", e.target.value)} />
                  </td>
                  <td>
                    <input type="number" style={{ width: 90 }} value={line.paidAmount} onChange={(e) => setLine(line.key, "paidAmount", e.target.value)} />
                  </td>
                  <td>
                    <input type="number" style={{ width: 80 }} disabled={line.duePayment} value={line.fineAmount} onChange={(e) => setLine(line.key, "fineAmount", e.target.value)} />
                  </td>
                  <td>
                    <select value={line.waiverId} onChange={(e) => setLine(line.key, "waiverId", e.target.value)}>
                      <option value="">None</option>
                      {feeWaivers.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="number" style={{ width: 80 }} value={line.waiverAmount} onChange={(e) => setLine(line.key, "waiverAmount", e.target.value)} />
                  </td>
                  <td>
                    {lines.length > 1 && (
                      <button type="button" className="btn-link danger" onClick={() => removeLine(line.key)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="form-actions" style={{ justifyContent: "space-between" }}>
            <button type="button" className="btn-link" onClick={addLine}>
              + Add Line
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Generating..." : "Generate Payslip"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

function BatchGenerateTab({ setError, setSuccess }) {
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [students, setStudents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [feeSubHeadIds, setFeeSubHeadIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([listClassConfigurations(), feeHeadApi.list(), feeSubHeadApi.list()]).then(([configs, heads, subHeads]) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
      setFeeHeads(heads);
      setFeeSubHeads(subHeads);
    });
  }, []);

  useEffect(() => {
    if (!classConfigurationId || !academicYear) return;
    listStudents(academicYear, classConfigurationId).then((list) => {
      setStudents(list);
      setSelectedIds([]);
    });
  }, [classConfigurationId, academicYear]);

  const toggleStudent = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    setSelectedIds(selectedIds.length === students.length ? [] : students.map((s) => s.identificationId));
  };
  const toggleSubHead = (id) => {
    setFeeSubHeadIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const cc = classConfigs.find((c) => String(c.id) === classConfigurationId);

  const handleGenerate = async () => {
    setError("");
    if (!cc || selectedIds.length === 0 || !feeHeadId || feeSubHeadIds.length === 0) {
      setError("Select a class, at least one student, a fee head and at least one sub-head.");
      return;
    }
    setSaving(true);
    try {
      const res = await batchGeneratePayslips({
        classId: cc.classId,
        identificationIds: selectedIds,
        feeHeads: [{ feeHeadId: Number(feeHeadId), feeSubHeadIds: feeSubHeadIds.map(Number) }],
      });
      setSuccess(res.message || "Payslips generated.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to generate payslips.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Academic Year
          <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} style={{ width: 90 }} />
        </label>
        <label>
          Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)}>
            <option value="">Select</option>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ margin: "12px 0" }}>
        <strong>Sub-Heads to include:</strong>{" "}
        {feeSubHeads.map((s) => (
          <label key={s.id} className="checkbox-label" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={feeSubHeadIds.includes(s.id)} onChange={() => toggleSubHead(s.id)} /> {s.name}
          </label>
        ))}
      </div>

      <DataTable
        columns={[
          {
            key: "select",
            label: <input type="checkbox" checked={students.length > 0 && selectedIds.length === students.length} onChange={toggleAll} />,
            render: (s) => <input type="checkbox" checked={selectedIds.includes(s.identificationId)} onChange={() => toggleStudent(s.identificationId)} />,
          },
          { key: "studentRoll", label: "Roll" },
          { key: "studentName", label: "Student" },
          { key: "customStudentId", label: "Student ID" },
        ]}
        rows={students}
      />

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleGenerate} disabled={saving}>
          {saving ? "Generating..." : `Generate for ${selectedIds.length} student(s)`}
        </button>
      </div>
    </>
  );
}

function CollectTab({ setError, setSuccess }) {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [debitLedgerId, setDebitLedgerId] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [collecting, setCollecting] = useState(false);

  const load = () => {
    setLoading(true);
    listPendingPayslips()
      .then(setPayslips)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    // Real old-app `payslip-collect.html`'s "Paid By" picker calls
    // `/general/accounts/report/cash-equivalance/ledger/list` — category_default_id
    // {102} only (Cash & Cash Equivalent, no Account Receivable) — found while
    // sweeping Fee Management (previously used the unfiltered `listLedgers`; see
    // MIGRATION.md).
    listCashOnlyLedgers().then((list) => {
      setLedgers(list);
      if (list.length > 0) setDebitLedgerId(String(list[0].ledgerId));
    });
  }, []);

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const handleDelete = async (payslip) => {
    if (!window.confirm(`Delete pending payslip for "${payslip.studentName}"?`)) return;
    try {
      await deletePayslip(payslip.payslipId);
      setSuccess("Payslip deleted.");
      setPayslips((prev) => prev.filter((p) => p.payslipId !== payslip.payslipId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete payslip.");
    }
  };

  const handleCollect = async () => {
    setError("");
    if (selectedIds.length === 0 || !debitLedgerId) {
      setError("Select at least one payslip and a ledger.");
      return;
    }
    setCollecting(true);
    try {
      const res = await collectPayslips({ debitLedgerId: Number(debitLedgerId), paymentDate, payslipIds: selectedIds });
      setSuccess(res.message || "Payslips collected.");
      setSelectedIds([]);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to collect payslips.");
    } finally {
      setCollecting(false);
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Debit Ledger
          <select value={debitLedgerId} onChange={(e) => setDebitLedgerId(e.target.value)}>
            {ledgers.map((l) => (
              <option key={l.ledgerId} value={l.ledgerId}>
                {l.ledgerName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Payment Date
          <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "select", label: "", render: (p) => <input type="checkbox" checked={selectedIds.includes(p.payslipId)} onChange={() => toggle(p.payslipId)} /> },
            { key: "studentName", label: "Student" },
            { key: "customStudentId", label: "Student ID" },
            { key: "payableAmount", label: "Payable" },
            { key: "paidAmount", label: "Paid" },
            { key: "createDate", label: "Created" },
          ]}
          rows={payslips}
          emptyMessage="No pending payslips."
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}

      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleCollect} disabled={collecting}>
          {collecting ? "Collecting..." : `Collect ${selectedIds.length} Payslip(s)`}
        </button>
      </div>
    </>
  );
}
